import React, { useMemo } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';
import css from './ListingCard.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import Icons from '../Icons/Icons';
import {
  addItemToFavorites,
  deleteItemFromFavorites,
  favoritesSelector,
} from '../../containers/FavoritesPage/FavoritesPage.duck';
import { isInstantBooking, truncateWords } from '../../util/genericHelpers';
import {
  getCategoryLabel,
  getListingCategoryLevel,
  getListingServiceArea,
  getListingType,
} from '../../util/dataExtractors';
import { ListingType, PricingType } from '../../util/enums';
import { priceData } from '../../util/helperFunctions';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routing/routeConfiguration';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const MAX_LENGTH_FOR_LONG_WORDS = 20;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

export const PriceMaybe = ({
  price,
  publicData: { listingType, pricingType, unitType, transactionProcessAlias } = {},
  config: {
    listing: { listingTypes },
    currency,
  },
  intl,
}) => {
  if (price?.amount === 0) {
    return (
      <div className={css.price}>
        <FormattedMessage id="ListingCard.requestToQuote" />
      </div>
    );
  }
  // 1) Hide if price exists but shouldn't be shown
  if (!displayPrice(listingTypes.find(l => l.listingType === listingType)) && price) {
    return null;
  }

  // 2) Prepare formatted price
  const { formattedPrice, priceTitle } = priceData(price, currency, intl);
  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;

  // 3) Default per‐unit element (if bookable)
  let pricePerUnit = isBookingProcessAlias(transactionProcessAlias) ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType }} />
    </span>
  ) : (
    ''
  );

  // 4) Override for service‐provider flat/hourly/job rates
  if (listingType === ListingType.SERVICE_PROVIDER && pricingType) {
    pricePerUnit =
      {
        [PricingType.FLAT_RATE]: 'Flat rate',
        [PricingType.HOURLY]: 'Hourly',
      }[pricingType] || 'Per job';
  }

  // 5) Single, consistent render
  return (
    <div className={css.price} title={priceTitle}>
      <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    isServiceListingCard,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description = '', price, publicData } = currentListing.attributes;
  const listingCategories = config.categoryConfiguration.categories;

  const ALLOWED_PARENT_IDS = useMemo(
    () => [
      'land-tree-services',
      'event-services-rentals',
      'vehicle-equipment-repair',
      'animal-care-farm-help',
      'transport-cleanup',
      'outdoor-carpentry-structures',
    ],
    []
  );

  // Filter the full list down to just those six
  const filteredCategories = useMemo(
    () => listingCategories.filter(cat => ALLOWED_PARENT_IDS.includes(cat.id)),
    [listingCategories, ALLOWED_PARENT_IDS]
  );
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const dispatch = useDispatch();
  const history = useHistory();
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const { favorites = [] } = useSelector(favoritesSelector);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAlreadyAddedToFavorites = Array.isArray(favorites) && favorites.includes(id);

  const handleFavorites = async type => {
    if (!isAuthenticated) {
      history.push(createResourceLocatorString('SignupPage', routeConfiguration(), {}, {}));
    }
    const action = type === 'unlike' ? deleteItemFromFavorites : addItemToFavorites;
    const payload =
      type === 'unlike'
        ? { isAuthenticated, id, favorites, config }
        : { favorites, selectedFavoriteId: id };

    try {
      await dispatch(action(payload));
    } catch (error) {
      console.error('Error handling favorites');
    }
  };
  const listingType = getListingType(currentListing);
  const categoryLevel = getListingCategoryLevel(currentListing);
  const categoryLabel = getCategoryLabel(categoryLevel, filteredCategories);
  const serviceArea = getListingServiceArea(currentListing, isServiceListingCard);
  return (
    <>
      <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />

          <div className={css.favouriteSec}>
            {isInstantBooking(listing) ? (
              <div className={css.guestFavorite}>
                <Icons name="instantBooking" /> <FormattedMessage id="ListingCard.instantBooking" />
              </div>
            ) : null}

            {listingType === ListingType.SERVICE_PROVIDER && (
              <div className={css.guestFavorite}>
                <FormattedMessage id="ListingCard.categoryLabel" values={{ categoryLabel }} />
              </div>
            )}
            <div className={css.favorite}>
              <div
                className={classNames(isAlreadyAddedToFavorites ? css.filled : css.icon)}
                onClick={e => {
                  handleFavorites(isAlreadyAddedToFavorites ? 'unlike' : 'like');
                  e.preventDefault();
                  e.stopPropagation();
                }}
                aria-label={
                  isAlreadyAddedToFavorites ? 'Remove from favorites' : 'Add to favorites'
                }
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleFavorites(isAlreadyAddedToFavorites ? 'unlike' : 'like');
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                <Icons name={isAlreadyAddedToFavorites ? 'favorite' : 'unfavorite'} />
              </div>
            </div>
          </div>
        </AspectRatioWrapper>
        <div className={css.info}>
          <div className={css.mainInfo}>
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>

            {/* <div className={css.ratings}>
                <Icons name="star" /> 4.97
              </div> */}
          </div>
          <div className={css.description}>
            {truncateWords(description, MAX_LENGTH_FOR_LONG_WORDS)}
          </div>
          {serviceArea && <div className={css.description}>{serviceArea}</div>}
          <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
        </div>
      </NamedLink>
    </>
  );
};

export default ListingCard;
